import { createRng, type SeededRandom } from "./utils";

export type AssignmentPart = 1 | 2 | 3;
export type AssignmentVariable = "A" | "B" | "C";

export type AssignmentSet = {
  seed: number;
  part: AssignmentPart;
  displays: [string, string, string];
  values: Record<AssignmentVariable, number>;
  promptOrder: AssignmentVariable[];
};

const variables: AssignmentVariable[] = ["A", "B", "C"];

function valueMap(rng: SeededRandom): Record<AssignmentVariable, number> {
  return { A: rng.int(1, 4), B: rng.int(1, 4), C: rng.int(1, 4) };
}

function relation(
  targetVar: AssignmentVariable,
  sourceVar: AssignmentVariable,
  target: number,
  source: number,
  rng: SeededRandom,
): string {
  const candidates: string[] = [];
  if (target === source) candidates.push(`${targetVar} = ${sourceVar}`);
  if (target > source) candidates.push(`${targetVar} = ${sourceVar} + ${target - source}`);
  if (target < source) candidates.push(`${targetVar} = ${sourceVar} - ${source - target}`);
  if (source !== 0 && target % source === 0 && target / source > 1) {
    candidates.push(`${targetVar} = ${sourceVar} × ${target / source}`);
  }
  if (target !== 0 && source % target === 0 && source / target > 1) {
    candidates.push(`${targetVar} = ${sourceVar} ÷ ${source / target}`);
  }
  return rng.pick(candidates.length ? candidates : [`${targetVar} = ${target}`]);
}

/** Same part and seed always produce the same values, displays, and prompt order. */
export function generateAssignmentSet(
  part: AssignmentPart,
  seed = part * 1009 + 17,
): AssignmentSet {
  const rng = createRng(seed);
  const values = valueMap(rng);
  const displays: Record<AssignmentVariable, string> = {
    A: `A = ${values.A}`,
    B: `B = ${values.B}`,
    C: `C = ${values.C}`,
  };

  if (part === 2) {
    const derived = rng.pick(variables);
    const sources = variables.filter((variable) => variable !== derived);
    const source = rng.pick(sources);
    displays[derived] = relation(derived, source, values[derived], values[source], rng);
  }

  if (part === 3) {
    const chain = rng.shuffle(variables);
    const root = chain[0]!;
    const second = chain[1]!;
    const third = chain[2]!;
    displays[root] = `${root} = ${values[root]}`;
    displays[second] = relation(second, root, values[second], values[root], rng);
    displays[third] = relation(third, second, values[third], values[second], rng);
  }

  return {
    seed,
    part,
    displays: [displays.A, displays.B, displays.C],
    values,
    promptOrder: rng.shuffle(variables),
  };
}
